import { zonedDate } from '../../utils/date-time';
import React, { ReactNode, useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { useAppContext } from '../../contexts/AppContext';
import PriceInput from '../../components/inputs/Price';
import { Modal } from '../../components/Modal';
import AvatarUser from '../../components/AvatarUser';
import ScheduleView from '../agenda/estabelecimento/view/ScheduleView';
import { StatusHorario } from '../agenda/Enum';
import { getScheduleStatusLabel, getScheduleTone } from '../agenda/cliente/List/helpers';
import { RecorrenciasService } from './Service';
import { describeRegra, formatMes, formatProximo, getEncerramentoLabel, getServiceNames, getSituacao } from './helpers';
import './Recorrencias.css';
import { Calendar, CircleHelp } from 'lucide-react';

const service = new RecorrenciasService();

type Aba = 'proximos' | 'anteriores';

const OPEN_STATUSES = [StatusHorario.PENDENTE, StatusHorario.AGENDADO];

const formatBRL = (value?: number) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const getAssinaturaStatusLabel = (status?: number) => {
  if (status === 1) return 'Ativa';
  if (status === 0) return 'Cancelada';
  return 'Status não informado';
};

const getHorarioPrice = (agendamento: any): number =>
  agendamento.totalPrice ?? agendamento.service?.price ?? agendamento.servico?.price ?? 0;

const InfoRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="sf-info-row">
    <span>{label}</span>
    <span>{children}</span>
  </div>
);

const DetalhesRecorrencia = () => {
  const { successSnackbar, errorSnackbar } = useAppContext();
  const { id } = useParams();
  const navigate = useNavigate();
  const [recorrencia, setRecorrencia] = useState<any>(null);
  const [agendamentos, setAgendamentos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [prices, setPrices] = useState<Record<string, number>>({});
  const [savingPriceId, setSavingPriceId] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState<any>(null);
  const [aba, setAba] = useState<Aba | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchRecorrencia = useCallback(async () => {
    try {
      const response = await service.getById(String(id));
      setRecorrencia(response.data);
    } catch (error) {
      console.error('Erro ao buscar recorrência:', error);
    }
  }, [id]);

  const fetchAgendamentos = useCallback(async () => {
    try {
      const { data } = await service.getHorariosByRecorrenciaId(String(id));
      setAgendamentos(data);
      setPrices(
        data.reduce((acc: Record<string, number>, agendamento: any) => {
          acc[agendamento._id] = getHorarioPrice(agendamento);
          return acc;
        }, {}),
      );
    } catch (error) {
      console.error('Erro ao buscar agendamentos:', error);
    }
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchRecorrencia(), fetchAgendamentos()]);
      setLoading(false);
    };
    fetchData();
  }, [id, fetchRecorrencia, fetchAgendamentos]);

  const { proximos, anteriores, concluidos, emAberto } = useMemo(() => {
    const now = moment();
    const isProximo = (agendamento: any) =>
      OPEN_STATUSES.includes(agendamento.status) && zonedDate(agendamento.startAt, agendamento.timezone).isSameOrAfter(now);

    return {
      proximos: agendamentos.filter(isProximo),
      anteriores: agendamentos.filter((agendamento) => !isProximo(agendamento)).reverse(),
      concluidos: agendamentos.filter(({ status }) => status === StatusHorario.CONCLUIDO).length,
      emAberto: agendamentos.filter(({ status }) => OPEN_STATUSES.includes(status)).length,
    };
  }, [agendamentos]);

  const abaAtiva: Aba = aba || (proximos.length || !anteriores.length ? 'proximos' : 'anteriores');
  const listaAtiva = abaAtiva === 'proximos' ? proximos : anteriores;
  const meses = useMemo(() => {
    const grupos: { key: string; label: string; items: any[] }[] = [];

    listaAtiva.forEach((agendamento) => {
      const key = zonedDate(agendamento.startAt, agendamento.timezone).format('YYYY-MM');
      let grupo = grupos.find((item) => item.key === key);

      if (!grupo) {
        grupo = { key, label: formatMes(agendamento.startAt, agendamento.timezone), items: [] };
        grupos.push(grupo);
      }

      grupo.items.push(agendamento);
    });

    return grupos;
  }, [listaAtiva]);

  const handleSavePrice = async (horarioId: string) => {
    try {
      setSavingPriceId(horarioId);
      await service.updateHorarioPrice(horarioId, prices[horarioId]);
      successSnackbar('Valor atualizado com sucesso!');
      await fetchAgendamentos();
    } catch (error) {
      errorSnackbar('Erro ao atualizar o valor.');
      console.error('Erro ao atualizar o valor.', error);
    } finally {
      setSavingPriceId('');
    }
  };

  const handleCloseSchedule = () => {
    setSelectedSchedule(null);
    fetchAgendamentos();
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await service.deleteRecorrencia(String(id));
      successSnackbar('Recorrência removida com sucesso.');
      navigate('/recorrencias');
    } catch (error) {
      errorSnackbar('Erro ao remover a recorrência.');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="rec-page space-y-3" aria-busy="true">
        <div className="sf-skeleton h-24" />
        <div className="sf-skeleton h-48" />
      </div>
    );
  }

  if (!recorrencia) {
    return (
      <div className="rec-page">
        <div className="sf-state">
          <CircleHelp aria-hidden />
          <h2>Recorrência não encontrada</h2>
          <p>Ela pode ter sido removida.</p>
          <button type="button" className="sf-link" onClick={() => navigate('/recorrencias')}>
            Voltar para recorrências
          </button>
        </div>
      </div>
    );
  }

  const [servico, ...extras] = getServiceNames(recorrencia);
  const situacao = getSituacao(recorrencia);
  const proximo = proximos[0];
  const assinatura = recorrencia.assinatura;

  return (
    <div className="rec-page">
      {selectedSchedule && (
        <Modal title="Agendamento" onClose={handleCloseSchedule}>
          <ScheduleView schedule={selectedSchedule} />
        </Modal>
      )}

      {openDelete && (
        <Modal onClose={() => setOpenDelete(false)} title="Remover recorrência?">
          <p className="text-sm text-base-content/70">
            {emAberto === 0
              ? 'Não há agendamentos em aberto para cancelar.'
              : emAberto === 1
                ? '1 agendamento em aberto será cancelado e o horário será liberado.'
                : `${emAberto} agendamentos em aberto serão cancelados e os horários serão liberados.`}{' '}
            Os atendimentos concluídos continuam no histórico.
            {assinatura && ' A assinatura continua ativa, sem esta recorrência.'}
          </p>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" className="btn btn-ghost" onClick={() => setOpenDelete(false)} disabled={deleting}>
              Cancelar
            </button>
            <button type="button" className="btn btn-error" onClick={handleDelete} disabled={deleting}>
              {deleting && <span className="loading loading-spinner loading-sm"></span>}
              Remover
            </button>
          </div>
        </Modal>
      )}

      <div className="rec-detail">
        <aside className="rec-detail-aside">
          <div className="sf-group rec-summary">
            <AvatarUser
              name={recorrencia.clientName || 'Cliente'}
              photoURL={recorrencia.clientPhotoURL}
              size="h-12 w-12 !text-base"
            />
            <div className="min-w-0">
              <h2 className="rec-summary-title">{recorrencia.clientName || 'Cliente removido'}</h2>
              <div className="rec-sub">{servico}</div>
              {extras.length > 0 && <div className="rec-sub">Extras: {extras.join(', ')}</div>}
            </div>
          </div>

          <div className="sf-group">
            <InfoRow label="Repetição">{describeRegra(recorrencia)}</InfoRow>
            <InfoRow label="Período">
              {zonedDate(recorrencia.startAt, recorrencia.timezone).format('DD/MM/YYYY')} a {zonedDate(recorrencia.endAt, recorrencia.timezone).format('DD/MM/YYYY')}
            </InfoRow>
            {situacao !== 'ativa' && (
              <InfoRow label="Situação">
                <span className="sf-tag" data-tone={situacao === 'encerrando' ? 'orange' : 'gray'}>
                  {getEncerramentoLabel(recorrencia)}
                </span>
              </InfoRow>
            )}
            <InfoRow label="Atendente">{recorrencia.attendantName}</InfoRow>
            <InfoRow label="Próximo">
              {proximo ? formatProximo(proximo.startAt, proximo.timezone || recorrencia.timezone) : 'Sem agendamentos futuros'}
            </InfoRow>
            <InfoRow label="Concluídos">
              <span className="rec-numeric">
                {concluidos} de {agendamentos.length}
              </span>
            </InfoRow>
          </div>

          {assinatura && (
            <div>
              <h3 className="sf-group-title">Assinatura vinculada</h3>
              <div className="sf-group">
                <InfoRow label="Plano">{assinatura.planName || 'Plano'}</InfoRow>
                <InfoRow label="Cobrança">
                  {assinatura.billingType === 'UNICO' ? 'Pagamento único' : 'Recorrente'} • {formatBRL(assinatura.price)}
                </InfoRow>
                <InfoRow label="Status">{getAssinaturaStatusLabel(assinatura.status)}</InfoRow>
                <InfoRow label="Desconto">
                  {Number(assinatura.appointmentDiscountPercent || 0).toLocaleString('pt-BR')}%
                </InfoRow>
                <InfoRow label="Início">
                  {assinatura.startAt ? zonedDate(assinatura.startAt, recorrencia.timezone).format('DD/MM/YYYY') : '—'}
                </InfoRow>
                <InfoRow label="Término">
                  {assinatura.endAt ? zonedDate(assinatura.endAt, recorrencia.timezone).format('DD/MM/YYYY') : 'Indeterminado'}
                </InfoRow>
                <InfoRow label="Próxima cobrança">
                  {assinatura.nextBillingAt ? zonedDate(assinatura.nextBillingAt, recorrencia.timezone).format('DD/MM/YYYY') : '—'}
                </InfoRow>
              </div>
            </div>
          )}

          <div className="sf-group">
            <button type="button" className="sf-destructive-row" onClick={() => setOpenDelete(true)}>
              Remover recorrência
            </button>
          </div>
        </aside>

        <section className="rec-detail-main" aria-label="Agendamentos da recorrência">
          <div className="sf-segmented rec-tabs" role="group" aria-label="Período dos agendamentos">
            <button type="button" aria-pressed={abaAtiva === 'proximos'} onClick={() => setAba('proximos')}>
              Próximos
              <span className="sf-segmented-count">{proximos.length}</span>
            </button>
            <button type="button" aria-pressed={abaAtiva === 'anteriores'} onClick={() => setAba('anteriores')}>
              Anteriores
              <span className="sf-segmented-count">{anteriores.length}</span>
            </button>
          </div>

          {meses.length === 0 ? (
            <div className="sf-state mt-4">
              <Calendar aria-hidden />
              <h2>
                {abaAtiva === 'proximos' ? 'Nenhum agendamento futuro' : 'Nenhum agendamento anterior'}
              </h2>
            </div>
          ) : (
            meses.map((mes) => (
              <div key={mes.key} className="mt-5">
                <h3 className="sf-group-title">{mes.label}</h3>
                <ul className="sf-group">
                  {mes.items.map((agendamento) => {
                    const start = zonedDate(agendamento.startAt, agendamento.timezone);
                    const isCancelled = agendamento.status === StatusHorario.CANCELADO;
                    const price = prices[agendamento._id];
                    const isDirty = price !== getHorarioPrice(agendamento);
                    const isSaving = savingPriceId === agendamento._id;
                    const serviceName = agendamento.service?.name || agendamento.servico?.name || 'Serviço';

                    return (
                      <li key={agendamento._id} className="rec-appt">
                        <button
                          type="button"
                          className="rec-appt-main"
                          onClick={() => setSelectedSchedule(agendamento)}
                        >
                          <span className="sf-date" aria-hidden>
                            <span>{start.format('ddd').replace('.', '')}</span>
                            <strong>{start.format('DD')}</strong>
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className={`rec-title ${isCancelled ? 'is-cancelled' : ''}`}>
                              <span className="sr-only">{start.format('dddd, DD/MM')} </span>
                              {start.format('HH:mm')} · {serviceName}
                              {agendamento.servicosExtras?.length ? ` +${agendamento.servicosExtras.length}` : ''}
                            </span>
                            <span className="sf-tags">
                              <span className="sf-tag" data-tone={getScheduleTone(agendamento.status)}>
                                {getScheduleStatusLabel(agendamento)}
                              </span>
                            </span>
                          </span>
                        </button>

                        <div className="rec-appt-price">
                          {isCancelled ? (
                            <span className="rec-sub rec-numeric">{formatBRL(price)}</span>
                          ) : (
                            <>
                              {isDirty && (
                                <button
                                  type="button"
                                  className="sf-link"
                                  disabled={isSaving || Number.isNaN(price)}
                                  onClick={() => handleSavePrice(agendamento._id)}
                                >
                                  {isSaving && <span className="loading loading-spinner loading-xs"></span>}
                                  Salvar
                                </button>
                              )}
                              <PriceInput
                                value={Number.isNaN(price) ? '' : price?.toString() || ''}
                                onChange={(e) =>
                                  setPrices((current) => ({
                                    ...current,
                                    [agendamento._id]: parseFloat(e.target.value),
                                  }))
                                }
                                className="rec-price-input"
                                placeholder="R$ 0,00"
                                label=""
                              />
                            </>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </section>
      </div>
    </div>
  );
};

export default DetalhesRecorrencia;
